import React, { useState, useEffect, useCallback } from 'react';
import { BsEye, BsCheck, BsCopy } from 'react-icons/bs';
import { Tag, Calendar, Layers } from 'lucide-react';
import './style.css';

// Components
import Table from '../../molecules/tablas/view';
import ButtonAction from '../../atoms/buttonAction/view';
import Chip from '../../atoms/chip/container';
import TooltipC from '../../atoms/tooltip/view';
import Modal from '../../atoms/modal/view';
import FacetedFilter from '../../molecules/FacetedFilter/view';
import ButtonBasic from '../../atoms/buttonBasic/view';

// Utils & Usecases
import { getRadianEvents, getRadianEventDetail, RADIAN_EVENTS } from '../../../usecases/radian-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';

const EVENT_TYPE_LABELS = {
  '030': 'Acuse de Recibo',
  '031': 'Rechazo de Factura',
  '032': 'Recibo del Bien o Servicio',
  '033': 'Aceptación Expresa',
  '034': 'Rechazo de Evento',
  '1': 'Acuse de Recibo',
  '2': 'Rechazo de Factura',
  '3': 'Recibo del Bien o Servicio',
  '4': 'Aceptación Expresa',
};

const EVENT_CHIP_TYPES = {
  '030': 'radian-030',
  '031': 'radian-031',
  '032': 'radian-032',
  '033': 'radian-033',
  '034': 'radian-034',
};

const STATUS_LABELS = {
  success: 'Emitida',
  Emitted: 'Emitida',
  pending: 'Pendiente',
  error: 'Error',
  Error: 'Error',
  failed: 'Error',
};

const normalizeEventCode = (code) => {
  if (!code) return '---';
  const clean = String(code).replace('event_', '').replace('03', '');
  if (clean === '0' || clean === '1') return '030';
  if (clean === '2') return '032';
  if (clean === '3') return '033';
  if (clean === '4') return '034';
  if (code.includes('030')) return '030';
  if (code.includes('031')) return '031';
  if (code.includes('032')) return '032';
  if (code.includes('033')) return '033';
  if (code.includes('034')) return '034';
  return code;
};

function RadianEventsTable() {
  const [events, setEvents] = useState({
    data: [],
    page: 1,
    page_size: 20,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const filtersConfig = [
    {
      key: 'status',
      label: 'Estado',
      icon: <Tag size={14} />,
      options: [
        { value: 'success', label: 'Emitida' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'error', label: 'Error' },
      ],
    },
    {
      key: 'event_code',
      label: 'Tipo de Evento',
      icon: <Layers size={14} />,
      options: [
        { value: '030', label: '030 - Acuse de Recibo' },
        { value: '031', label: '031 - Reclamo / Rechazo' },
        { value: '032', label: '032 - Recibo del Bien o Servicio' },
        { value: '033', label: '033 - Aceptación Expresa' },
        { value: '034', label: '034 - Rechazo de Evento' },
      ],
    },
    { key: 'date_from', label: 'Fecha Desde', icon: <Calendar size={14} />, type: 'date' },
    { key: 'date_to', label: 'Fecha Hasta', icon: <Calendar size={14} />, type: 'date' },
  ];

  const getFilterParams = (filtersList) => {
    let status = undefined;
    let event_code = undefined;
    let date_from = undefined;
    let date_to = undefined;

    (filtersList || []).forEach((item) => {
      if (item.key === 'status') status = item.value;
      if (item.key === 'event_code') event_code = item.value;
      if (item.key === 'date_from') date_from = item.value;
      if (item.key === 'date_to') date_to = item.value;
    });

    return { status, event_code, date_from, date_to };
  };

  const fetchEvents = useCallback(
    async (page = 1, limit = 20, appliedFilters = activeFilters, search = searchQuery) => {
      setIsLoading(true);
      try {
        const filterParams = getFilterParams(appliedFilters);
        if (search && search.trim()) {
          filterParams.search = search.trim();
        }
        const result = await getRadianEvents(page, limit, filterParams);
        const eventList = result?.data || result?.events || (Array.isArray(result) ? result : []);
        setEvents({
          data: eventList,
          page: result?.page || page,
          page_size: result?.page_size || result?.limit || limit,
          total: result?.total !== undefined ? result.total : eventList.length,
        });
      } catch (error) {
        console.error('[RADIAN] Error fetching events:', error);
        dispatchCustomEvent('popup', {
          message: 'Error al cargar los eventos RADIAN',
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [activeFilters, searchQuery]
  );

  useEffect(() => {
    fetchEvents(1, events.page_size, activeFilters, searchQuery);
  }, []);

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    fetchEvents(1, events.page_size, newFilters, searchQuery);
  };

  const handleSearch = (term) => {
    setSearchQuery(term);
    fetchEvents(1, events.page_size, activeFilters, term);
  };

  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleViewDetail = async (row) => {
    const item = row._original || row;
    try {
      const detail = await getRadianEventDetail(item.id);
      setSelectedEvent(detail || item);
    } catch (e) {
      setSelectedEvent(item);
    } finally {
      dispatchCustomEvent('loading-btn');
    }
    dispatchCustomEvent('modalradian-event-detail');
  };

  const getChipType = (state) => {
    const types = {
      success: 'success',
      Emitted: 'success',
      pending: 'warning',
      error: 'error',
      Error: 'error',
      failed: 'error',
    };
    return types[state] || 'disable';
  };

  const mappedData = (events.data || []).map((item) => {
    const normCode = normalizeEventCode(item.event_code || item.document_type);
    const eventName = EVENT_TYPE_LABELS[normCode] || RADIAN_EVENTS[normCode]?.name || item.event_code || 'Evento';
    const chipType = EVENT_CHIP_TYPES[normCode] || 'info';
    const statusText = STATUS_LABELS[item.status] || item.status || 'Desconocido';

    return {
      date: item.created_at || item.date || '---',
      invoice: item.invoice_number || '---',
      event: (
        <Chip type={chipType}>
          {normCode} - {eventName}
        </Chip>
      ),
      receiver: item.receiver_name
        ? `${item.receiver_name} (${item.receiver_nit || ''})`
        : item.receiver_nit || item.issuer_nit || '---',
      status: item.error_message ? (
        <TooltipC text={item.error_message}>
          <Chip type={getChipType(item.status)}>{statusText}</Chip>
        </TooltipC>
      ) : (
        <Chip type={getChipType(item.status)}>{statusText}</Chip>
      ),
      cude: item.cude ? (
        <div className="radian-cude-cell">
          <span>{item.cude}</span>
          <button
            className="radian-copy-btn"
            title="Copiar CUDE"
            onClick={(e) => {
              e.stopPropagation();
              handleCopy(item.cude, `cude-${item.id}`);
            }}
          >
            {copiedKey === `cude-${item.id}` ? <BsCheck size={14} color="#10b981" /> : <BsCopy size={12} />}
          </button>
        </div>
      ) : (
        '---'
      ),
      _original: item,
    };
  });

  return (
    <div className="radian-events-container">
      <div className="radian-events-toolbar">
        <FacetedFilter
          filters={filtersConfig}
          activeFilters={activeFilters}
          onChange={handleFilterChange}
          onClear={() => handleFilterChange([])}
        />
      </div>

      <Table
        id="radian-events-table"
        headersList={[
          'Fecha',
          'Factura Ref',
          'Tipo de Evento',
          'Tercero',
          'Estado',
          'CUDE',
        ]}
        paginatedData={{
          data: mappedData,
          page: events.page,
          page_size: events.page_size,
          total: events.total,
        }}
        getFunction={(page, limit) => fetchEvents(page, limit, activeFilters, searchQuery)}
        excludeList={['_original']}
        isLoading={isLoading}
        allowPageSizeControl={true}
        allowSearch={true}
        onSearch={handleSearch}
        formats={{
          date: { type: 'timestamp' },
        }}
      >
        <ButtonAction
          btnKey="radian-event-show-detail"
          title={<BsEye />}
          onClick={handleViewDetail}
        />
      </Table>

      {/* Modal de Detalle */}
      <Modal id="radian-event-detail" className="radian-detail-modal-wrapper">
        {selectedEvent && (
          <div className="radian-detail-modal-content">
            <div className="radian-detail-header">
              <div>
                <h3 className="radian-detail-title">
                  Evento {normalizeEventCode(selectedEvent.event_code || selectedEvent.document_type)} -{' '}
                  {EVENT_TYPE_LABELS[normalizeEventCode(selectedEvent.event_code || selectedEvent.document_type)] ||
                    'Detalle del Evento'}
                </h3>
                <p className="radian-detail-subtitle">ID Registro: #{selectedEvent.id} • Fecha: {selectedEvent.created_at || selectedEvent.date}</p>
              </div>
              <Chip type={getChipType(selectedEvent.status)}>{STATUS_LABELS[selectedEvent.status] || selectedEvent.status}</Chip>
            </div>

            <div className="radian-detail-grid">
              <div className="radian-detail-card">
                <span className="radian-detail-label">Factura Referenciada</span>
                <span className="radian-detail-val">{selectedEvent.invoice_number || '---'}</span>
              </div>
              <div className="radian-detail-card">
                <span className="radian-detail-label">Receptor / Proveedor</span>
                <span className="radian-detail-val">
                  {selectedEvent.receiver_name || ''} {selectedEvent.receiver_nit ? `(${selectedEvent.receiver_nit})` : '---'}
                </span>
              </div>
              <div className="radian-detail-card" style={{ gridColumn: 'span 2' }}>
                <span className="radian-detail-label">CUFE de la Factura</span>
                <div className="radian-detail-val">
                  <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedEvent.dian_code || selectedEvent.cufe || '---'}</span>
                  {(selectedEvent.dian_code || selectedEvent.cufe) && (
                    <button
                      className="radian-copy-btn"
                      onClick={() => handleCopy(selectedEvent.dian_code || selectedEvent.cufe, 'detail-cufe')}
                    >
                      {copiedKey === 'detail-cufe' ? <BsCheck size={16} color="#10b981" /> : <BsCopy size={14} />}
                    </button>
                  )}
                </div>
              </div>
              <div className="radian-detail-card" style={{ gridColumn: 'span 2' }}>
                <span className="radian-detail-label">CUDE del Evento</span>
                <div className="radian-detail-val">
                  <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedEvent.cude || '---'}</span>
                  {selectedEvent.cude && (
                    <button
                      className="radian-copy-btn"
                      onClick={() => handleCopy(selectedEvent.cude, 'detail-cude')}
                    >
                      {copiedKey === 'detail-cude' ? <BsCheck size={16} color="#10b981" /> : <BsCopy size={14} />}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {selectedEvent.error_message && (
              <div className="radian-detail-error">
                <strong>Error en DIAN/Plemsi:</strong>
                <p style={{ margin: '4px 0 0 0' }}>{selectedEvent.error_message}</p>
              </div>
            )}

            {selectedEvent.data && (
              <div className="radian-detail-json-section">
                <span className="radian-detail-label">Payload & Respuesta (JSON)</span>
                <pre className="radian-json-box">
                  {JSON.stringify(selectedEvent.data, null, 2)}
                </pre>
              </div>
            )}

            <div className="radian-detail-actions">
              <ButtonBasic
                title="Cerrar"
                id="btn-close-radian-detail"
                onClick={() => dispatchCustomEvent('close-modal')}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default RadianEventsTable;
